import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { FiEye, FiEyeOff } from "react-icons/fi"; // 🗸 React Icons

function OtpInput({ value, onChange, disabled }) {
  const DIGITS = 6;
  const inputRefs = useRef([]);

  if (inputRefs.current.length !== DIGITS) {
    inputRefs.current = Array.from({ length: DIGITS }, () => React.createRef());
  }

  const handlePaste = (e) => {
    const text = e.clipboardData
      .getData("Text")
      .trim()
      .replace(/[^0-9]/g, "")
      .slice(0, DIGITS);
    if (text.length === DIGITS) {
      onChange(text);
      inputRefs.current[DIGITS - 1].current?.focus();
      e.preventDefault();
    }
  };

  const handleChange = (e, idx) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 1) val = val.slice(-1);
    const newValue = value.split("");
    newValue[idx] = val;
    onChange(newValue.join("").padEnd(DIGITS, ""));
    if (val && idx < DIGITS - 1) inputRefs.current[idx + 1].current?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) {
      inputRefs.current[idx - 1].current?.focus();
    }
  };

  return (
    <div
      className="
        inline-flex
        justify-center
        gap-2
        sm:gap-3
        w-full
        flex-wrap
        sm:flex-nowrap
      "
    >
      {Array.from({ length: DIGITS }).map((_, i) => (
        <div
          key={i}
          className="
            inline-flex items-center p-2.5 relative
            rounded-lg border-[none]
            before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-lg 
            before:[background:linear-gradient(356deg,rgba(45,40,66,1)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,1)_97%)] 
            before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] 
            before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none
          "
        >
          <input
            className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl bg-transparent outline-none text-[#bebebe]"
            type="text"
            maxLength={1}
            ref={inputRefs.current[i]}
            value={value[i] || ""}
            onChange={(e) => handleChange(e, i)}
            onPaste={handlePaste}
            onKeyDown={(e) => handleKeyDown(e, i)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus={i === 0}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
}


function OtpVerificationBox({
  email,
  otp,
  setOtp,
  onVerify,
  minutes,
  seconds,
  loading,
  resendDisabled,
  onResend,
}) {
  return (
    <div className="relative w-full max-w-xl mx-auto mt-10 px-4">
      <div className="flex flex-col w-full items-center justify-end gap-6 sm:gap-8 p-4 sm:p-10 md:p-16 rounded-3xl border-t-[6px] border-b-[6px] border-none relative
        before:content-[''] before:absolute before:inset-0 before:p-1.5 before:rounded-3xl
        before:[background:linear-gradient(90deg,rgba(45,40,66,1)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,1)_97%)]
        before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]
        before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none bg-[#2a223a]"
      >
        <div className="inline-flex flex-col items-center gap-6 sm:gap-8 relative z-10 w-full">
          {/* Logo */}
          <div className="relative w-[161.09px] h-16 mb-4">
            <img
              className="absolute w-[50px] h-16 top-0 left-0"
              alt="Logo"
              src="https://c.animaapp.com/md7w1vbr66bQVi/img/freepik--the-style-is-candid-image-photography-with-natural--633.png"
            />
            <div className="absolute w-[108px] h-[51px] top-[13px] left-[57px]">
              <div className="relative w-[104px] h-[51px]">
                <div className="absolute top-0 left-0 bg-[linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)]
                  [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent]
                  font-normal text-transparent text-[25.4px] leading-[25.4px] whitespace-nowrap">
                  BABY
                </div>
                <div className="absolute top-[25px] left-0 bg-[linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)]
                  [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent]
                  font-normal text-transparent text-[25.4px] leading-[25.4px] whitespace-nowrap">
                  UNICORN
                </div>
              </div>
            </div>
          </div>

          {/* Title & Instruction */}
          <div className="inline-flex flex-col items-center gap-2 sm:gap-3 px-2 text-center">
            <div className="font-semibold text-white text-2xl sm:text-[32px]">
              Verification
            </div>
            <p className="font-normal text-[#bebebe] text-base sm:text-[22px]">
              Enter the code we sent to your email
            </p>
            <p className="font-bold text-[#bebebe] text-sm sm:text-[18px] break-all">
              <span className="text-[#6cc9ff]">{email}</span>
            </p>
          </div>

          {/* OTP Input */}
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />

          {/* Timer */}
          <div className="inline-flex flex-col items-center justify-center gap-3 sm:gap-4">
            <p className="text-neutral-100 text-sm sm:text-xl text-center">
              Your code will expire in
            </p>

            {/* Timer Box */}
            <div className="flex w-full max-w-[218px] h-[104px] items-center justify-between p-4 sm:p-6 bg-[#3a335066]
              rounded-xl border-r border-l border-dashed border-[#aa81ff] backdrop-blur-[10px] relative">
              <div className="flex flex-col w-[54px] items-center">
                <div className="font-bold text-white text-[32px] sm:text-[42px] text-center">{minutes}</div>
                <div className="font-normal text-neutral-100 text-xs sm:text-lg text-center">Minutes</div>
              </div>
              <div className="font-bold text-white text-[32px] sm:text-5xl text-center">:</div>
              <div className="flex flex-col w-[54px] items-center">
                <div className="font-bold text-white text-[32px] sm:text-[42px] text-center">
                  {seconds.toString().padStart(2, "0")}
                </div>
                <div className="font-normal text-neutral-100 text-xs sm:text-lg text-center">Second</div>
              </div>
            </div>

            {/* Resend Button */}
            <button
              className="text-xs sm:text-sm text-[#aa81ff] hover:underline disabled:opacity-50 mt-1 sm:mt-2"
              onClick={onResend}
              disabled={resendDisabled || loading}
              type="button"
            >
              {resendDisabled
                ? `Resend OTP in ${minutes}:${seconds.toString().padStart(2, "")}`
                : "Resend OTP"}
            </button>
          </div>

          {/* Verify Button */}
          <button
            onClick={onVerify}
            className="flex h-[50px] sm:h-[60px] items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 w-full
              rounded-[50px] border border-white shadow-[0px_4px_16px_#ff49b8]
              bg-[linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)]
              font-semibold text-white text-base sm:text-lg disabled:opacity-50"
            disabled={loading || otp.length !== 6}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>

      {/* Background Blurs – hidden on small screens */}
      <div className="pointer-events-none hidden sm:block">
        <div className="w-[276px] h-[276px] top-0 right-0 sm:left-[611px] bg-[#ff75b6] rounded-full absolute blur-[400px] -z-10" />
        <div className="w-[291px] h-[291px] bottom-0 left-0 bg-[#ff81bc] rounded-full absolute blur-[400px] -z-10" />
      </div>
    </div>
  );
}


export function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [timer, setTimer] = useState(120);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Password visibility toggles
const [showPassword, setShowPassword] = useState(false); // hidden initially
const [showConfirmPassword, setShowConfirmPassword] = useState(false); // hidden initially

  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  useEffect(() => {
    if (otpSent && !otpVerified && timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [otpSent, otpVerified, timer]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendOTP = async () => {
    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://babyu-sale.tech/backend_api/send-reset-link.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.status === "success") {
        toast.success("OTP sent to your email");
        setOtpSent(true);
        setOtp("");
        setOtpVerified(false);
        setResetToken("");
        setTimer(120);
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Enter all 6 digits");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://babyu-sale.tech/backend_api/verify-otp.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (data.status === "success" && data.reset_token) {
        toast.success("OTP verified!");
        setOtpVerified(true);
        setResetToken(data.reset_token);
      } else {
        toast.error(data.message || "OTP verification failed");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://babyu-sale.tech/backend_api/reset-password.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, newPassword }),
      });
      const data = await res.json();
      if (data.status === "success") {
        toast.success("Password reset successful!");
        setOtpSent(false);
        setOtp("");
        setOtpVerified(false);
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(data.message || "Reset failed");
      }
    } catch (err) {
      toast.error("Error resetting password");
    }
    setLoading(false);
  };


  

  return (
    <div className="flex w-full h-screen bg-[#1e1a2e]">
      {/* Left Side Image */}
 <div className="hidden lg:block w-1/2 relative bg-[url('https://c.animaapp.com/md94p8pusgmtt1/img/2151800485.png')] bg-cover bg-center">
  {/* Gradient overlay */}
  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1e1a2e]" />

  {/* Text overlay - moved up */}
<div className="absolute bottom-28 left-10 z-10">
  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
    Welcome back our
  </h2>
  <h1
    className="text-4xl md:text-5xl font-extrabold bg-clip-text text-transparent"
    style={{
      backgroundImage:
        "linear-gradient(90deg, rgba(255,110,179,1) 0%, rgba(170,129,255,1) 47%, rgba(107,201,255,1) 100%)",
    }}
  >
    Baby Unicorn AI
  </h1>
</div>

</div>


      {/* Right Side Form Area */}
      <div className="flex flex-1 items-center justify-center px-4">
        {!otpSent ? (
          <>
            {/* Initial Email Entry */}
            <div className="w-full max-w-lg p-10 rounded-3xl bg-[#2a223a] relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:p-1.5 before:rounded-3xl before:[background:linear-gradient(90deg,rgba(45,40,66,1)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,1)_97%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
              <div className="flex flex-col items-center gap-6 z-10 relative">
                <div className="relative w-[161.09px] h-16">
                  <img
                    className="absolute w-[50px] h-16 top-0 left-0"
                    alt="Freepik the style is"
                    src="https://c.animaapp.com/md94p8pusgmtt1/img/freepik--the-style-is-candid-image-photography-with-natural--633.png"
                  />

                  <div className="absolute w-[108px] h-[51px] top-[13px] left-[57px]">
                    <div className="relative w-[104px] h-[51px]">
                      <img
                        className="absolute w-10 h-px top-[19px] left-[61px] object-cover"
                        alt="Line"
                        src="https://c.animaapp.com/md94p8pusgmtt1/img/line-33.svg"
                      />

                      <div className="absolute w-[104px] h-[51px] top-0 left-0">
                        <div className="absolute top-0 left-0 bg-[linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)] [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent] text-transparent text-[25.4px] tracking-[0] leading-[25.4px] whitespace-nowrap">
                          BABY
                        </div>
                        <div className="absolute top-[25px] left-0 bg-[linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)] [-webkit-background-clip:text] bg-clip-text [-webkit-text-fill-color:transparent] text-transparent text-[25.4px] tracking-[0] leading-[25.4px] whitespace-nowrap">
                          UNICORN
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-white">Reset Your Password</h2>
                <p className="text-lg text-[#bebebe] text-center">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
                <div className="flex items-center gap-2 p-3.5 w-full bg-[#3a335066] rounded-md backdrop-blur-md lex h-[60px] items-center justify-between p-3.5 bg-[#3a335066] rounded-md backdrop-blur-[10px] relative before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-md before:[background:linear-gradient(356deg,rgba(45,40,66,0)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,0)_97%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent text-[#bebebe] outline-none placeholder-[#bebebe] w-full"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <button
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#ff6eb3] via-[#aa81ff] to-[#6cc9ff] text-white font-semibold px-8 py-3 rounded-full shadow-md"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
                {/* Cancel Button */}
                <div className="flex flex-col items-start gap-6 relative self-stretch w-full flex-[0_0_auto]">
                  <div className="flex h-[60px] items-center justify-center gap-2 px-3 py-3.5 relative self-stretch w-full rounded-[50px] border-[none] before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-[50px] before:[background:linear-gradient(90deg,rgba(255,110,179,1)_0%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_100%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
                    <a className="relative w-fit font-normal text-dark-100 text-lg text-center tracking-[0] leading-6 whitespace-nowrap text-white" href="/login">
                      Cancel
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : otpSent && !otpVerified ? (
          <OtpVerificationBox
            email={email}
            otp={otp}
            setOtp={setOtp}
            onVerify={handleVerifyOTP}
            minutes={minutes}
            seconds={seconds}
            loading={loading}
            resendDisabled={timer > 0}
            onResend={handleSendOTP}
          />
        ) : (
         <div className="relative w-full max-w-[887px] mx-auto px-4 py-8">
  <div
    className="flex flex-col w-full max-w-[624px] items-center justify-end gap-8 p-6 sm:p-10 md:p-16 mx-auto rounded-3xl border-[none]
    before:content-[''] w-full max-w-lg p-10 rounded-3xl bg-[#2a223a] relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:p-1.5 before:rounded-3xl before:[background:linear-gradient(90deg,rgba(45,40,66,1)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,1)_97%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none"
  >
    {/* Header + Logo */}
    <div className="inline-flex flex-col items-center gap-8 relative">
      <div className="relative w-[161.09px] h-16">
        <img
          className="absolute w-[50px] h-16 top-0 left-0"
          alt="Logo"
          src="https://c.animaapp.com/md94p8pusgmtt1/img/freepik--the-style-is-candid-image-photography-with-natural--633.png"
        />
        <div className="absolute w-[108px] h-[51px] top-[13px] left-[57px]">
          <div className="relative w-[104px] h-[51px]">
            <img
              className="absolute w-10 h-px top-[19px] left-[61px] object-cover"
              alt="Line"
              src="https://c.animaapp.com/md94p8pusgmtt1/img/line-33.svg"
            />
            <div className="absolute top-0 left-0 text-[25.4px] font-normal leading-[25.4px] whitespace-nowrap bg-gradient-to-r from-[#ff6eb3] via-[#aa81ff] to-[#6cc9ff] bg-clip-text text-transparent">
              BABY
            </div>
            <div className="absolute top-[25px] left-0 text-[25.4px] font-normal leading-[25.4px] whitespace-nowrap bg-gradient-to-r from-[#ff6eb3] via-[#aa81ff] to-[#6cc9ff] bg-clip-text text-transparent">
              UNICORN
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-center gap-3">
        <div className="text-white text-[28px] sm:text-[32px] font-semibold text-center">RESET PASSWORD</div>
        <p className="text-[#bebebe] text-[18px] sm:text-[22px] text-center">
          Let's reset your password carefully
        </p>
      </div>
    </div>

    {/* Password Inputs */}
    <div className="flex flex-col gap-6 w-full">
      {/* New Password */}
      <div className="flex h-[60px] items-center justify-between p-3.5 bg-[#3a335066] rounded-md backdrop-blur-[10px] relative before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-md before:[background:linear-gradient(356deg,rgba(45,40,66,0)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,0)_97%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
        <input
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Reset your password"
          disabled={loading}
          className="bg-transparent w-full text-[#bebebe] outline-none text-lg pr-8"
        />
        <span onClick={() => setShowPassword((v) => !v)} className="cursor-pointer ml-2 flex items-center">
          {showPassword ? <FiEye size={22} /> : <FiEyeOff size={22} />}
        </span>
      </div>

      {/* Confirm Password */}
      <div className="flex h-[60px] items-center justify-between p-3.5 bg-[#3a335066] rounded-md backdrop-blur-[10px] relative before:content-[''] before:absolute before:inset-0 before:p-px before:rounded-md before:[background:linear-gradient(356deg,rgba(45,40,66,0)_2%,rgba(255,110,179,1)_16%,rgba(170,129,255,1)_47%,rgba(107,201,255,1)_83%,rgba(50,43,70,0)_97%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none">
        <input
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
          disabled={loading}
          className="bg-transparent w-full text-[#bebebe] outline-none text-lg pr-8"
        />
        <span onClick={() => setShowConfirmPassword((v) => !v)} className="cursor-pointer ml-2 flex items-center">
          {showConfirmPassword ? <FiEye size={22} /> : <FiEyeOff size={22} />}
        </span>
      </div>
    </div>

    {/* Submit Button */}
    <div
      className={`flex h-[60px] items-center justify-center mt-6 px-8 py-4 w-full rounded-[50px] border-2 border-white shadow-[0px_4px_16px_#ff49b8] bg-gradient-to-r from-[#ff6eb3] via-[#aa81ff] to-[#6cc9ff] cursor-pointer ${loading ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={handleResetPassword}
    >
      <span className="text-white text-lg font-semibold">
        {loading ? 'Resetting...' : 'Submit'}
      </span>
    </div>
  </div>

  {/* Background Blur Decor */}
  <div className="absolute inset-0 w-full h-full pointer-events-none z-[-1]">
    <div className="w-[200px] sm:w-[276px] h-[200px] sm:h-[276px] top-0 left-[60%] sm:left-[611px] bg-[#ff75b6] rounded-full absolute blur-[250px] sm:blur-[400px]" />
    <div className="w-[200px] sm:w-[291px] h-[200px] sm:h-[291px] bottom-0 left-0 bg-[#ff81bc] rounded-full absolute blur-[250px] sm:blur-[400px]" />
  </div>
</div>

        )}
      </div>
    </div>
  );
}
